import { type FormEvent, useMemo, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import styled from "styled-components"
import SvgLogo from "../../../app/assets/img/logo.svg"
import api from "@utils/api.client"
import loginImage from "../../../app/assets/img/login-image.jpg"

const Page = styled.main`
  min-height: 100svh;
  display: grid;
  place-items: center;
  padding: clamp(1.25rem, 5vw, 4rem);
  background:
    linear-gradient(135deg, rgba(6, 27, 49, 0.96), rgba(12, 54, 87, 0.88)),
    url(${loginImage}) center / cover,
    #071a2e;
`

const Card = styled.section`
  width: min(580px, 100%);
  padding: clamp(1.5rem, 5vw, 3.5rem);
  border: 1px solid rgba(170, 205, 229, 0.45);
  border-top: 4px solid #4f9bd1;
  background: #fff;
  box-shadow: 0 35px 90px rgba(0, 12, 25, 0.38);

  svg {
    width: 200px;
    height: auto;
    margin-bottom: 2.4rem;
  }

  h1 {
    margin: 0;
    color: #102f4f;
    font-size: clamp(2rem, 7vw, 3.4rem);
    line-height: 1;
    letter-spacing: -0.05em;
  }

  > p {
    margin: 1rem 0 1.8rem;
    color: #5d7183;
    line-height: 1.65;
  }
`

const Form = styled.form`
  display: grid;
  gap: 1rem;

  label {
    display: grid;
    gap: 0.45rem;
    color: #173751;
    font-size: 0.86rem;
    font-weight: 750;
  }

  input {
    width: 100%;
    min-height: 54px;
    padding: 0 0.9rem;
    border: 1px solid #b9c8d4;
    border-radius: 2px;
    color: #102f4a;
    font: inherit;
  }

  input:focus {
    outline: 4px solid rgba(51, 122, 189, 0.13);
    border-color: #337abd;
  }

  button {
    min-height: 56px;
    border: 1px solid #1d6da6;
    border-radius: 2px;
    background: #1d6da6;
    color: #fff;
    font: inherit;
    font-weight: 800;
    cursor: pointer;
  }
`

const Notice = styled.p<{ $error?: boolean }>`
  margin: 1rem 0 0 !important;
  padding: 0.9rem 1rem;
  border-left: 3px solid ${({ $error }) => ($error ? "#be5937" : "#438663")};
  background: ${({ $error }) => ($error ? "#fff3ed" : "#eef8f2")};
  color: ${({ $error }) => ($error ? "#7b3a25" : "#285b40")} !important;
  font-size: 0.88rem;
`

const Back = styled(Link)`
  display: inline-block;
  margin-top: 1.5rem;
  color: #275f8e;
  font-size: 0.86rem;
  font-weight: 750;
  text-decoration: none;
`

interface PasswordAccessProps {
  mode: "forgot" | "reset"
}

const PasswordAccess = ({ mode }: PasswordAccessProps) => {
  const [params] = useSearchParams()
  const token = params.get("jeton") || ""
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmation, setConfirmation] = useState("")
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState("")
  const [error, setError] = useState("")
  const resetMode = mode === "reset"

  const passwordIssue = useMemo(() => {
    if (!password) return ""
    if (password.length < 10) return "Utilisez au moins 10 caractères."
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) return "Ajoutez au moins une lettre et un chiffre."
    if (confirmation && password !== confirmation) return "Les deux mots de passe doivent être identiques."
    return ""
  }, [confirmation, password])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError("")
    setMessage("")
    if (resetMode && (!token || passwordIssue || password !== confirmation)) {
      setError(!token ? "Le lien est incomplet. Demandez un nouveau lien." : passwordIssue || "Confirmez le mot de passe.")
      return
    }
    setBusy(true)
    try {
      const response = resetMode
        ? await api.post<{ message: string }>("/auth/reset-password", { token, password })
        : await api.post<{ message: string }>("/auth/forgot-password", { email })
      setMessage(response.data.message)
    } catch (requestError: unknown) {
      setError((requestError as { message?: string })?.message || "La demande n’a pas pu être traitée.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <Page>
      <Card>
        <Link to="/" aria-label="TB Gestion Immobilière — accueil">
          <SvgLogo />
        </Link>
        <h1>{resetMode ? "Choisir votre mot de passe" : "Retrouver votre accès"}</h1>
        <p>
          {resetMode
            ? "Définissez un mot de passe unique pour activer ou récupérer votre accès Espace TB."
            : "Entrez l’adresse courriel associée à Espace TB. Si elle est reconnue, nous vous transmettrons un lien valide pendant une heure."}
        </p>
        {!message && (
          <Form onSubmit={submit}>
            {resetMode ? (
              <>
                <label>
                  Nouveau mot de passe
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                </label>
                <label>
                  Confirmer le mot de passe
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={confirmation}
                    onChange={(event) => setConfirmation(event.target.value)}
                    required
                  />
                </label>
              </>
            ) : (
              <label>
                Adresse courriel
                <input
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </label>
            )}
            <button type="submit" disabled={busy}>
              {busy ? "Traitement…" : resetMode ? "Enregistrer le mot de passe" : "Envoyer le lien"}
            </button>
          </Form>
        )}
        {passwordIssue && resetMode && <Notice $error>{passwordIssue}</Notice>}
        {error && (
          <Notice $error role="alert">
            {error}
          </Notice>
        )}
        {message && <Notice role="status">{message}</Notice>}
        <Back to="/se-connecter">← Retour à la connexion</Back>
      </Card>
    </Page>
  )
}

export default PasswordAccess
